import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useBooking, useFolio } from '../api/queries';
import { buildReceipt } from '../receipt';
import { DEPARTMENT_LABEL, formatDate, formatDateTime, formatGhs } from '../format';
import { Button, ErrorNote, Icon, Loading } from '../ui/kit';
import { ReceiptBrand } from '../ui/ReceiptBrand';

/**
 * A print-ready receipt / invoice. "Print" opens the browser's print dialog, which can also save a PDF,
 * on a phone, tablet or computer, and on A4 or a narrow receipt printer. No server PDF library is needed.
 */
export function Receipt() {
  const { id = '' } = useParams();
  const { me } = useAuth();
  const booking = useBooking(id);
  const folio = useFolio(id);

  if (booking.isLoading || folio.isLoading) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }
  if (booking.error || folio.error || !booking.data || !folio.data) {
    return (
      <div className="page">
        <ErrorNote error={booking.error ?? folio.error} retry={() => { void booking.refetch(); void folio.refetch(); }} />
      </div>
    );
  }

  const b = booking.data;
  const r = buildReceipt(folio.data.entries);
  const settled = r.balance === 0 && r.gross > 0;
  const title = settled ? 'Receipt' : 'Invoice';
  const p = me?.property;

  return (
    <div className="page receipt-page">
      <div className="no-print row" style={{ justifyContent: 'space-between' }}>
        <Link to={`/bookings/${id}`} className="row small" style={{ textDecoration: 'none' }}>
          <span style={{ width: 18, height: 18 }}>
            <Icon name="back" />
          </span>
          Back to booking
        </Link>
        <Button variant="primary" onClick={() => window.print()}>
          Print or save as PDF
        </Button>
      </div>

      <article className="receipt" aria-label={title}>
        <header className="receipt-head">
          <ReceiptBrand property={p} />
          <div className="receipt-meta">
            <div className="receipt-title">{title}</div>
            <div className="num">{b.reference}</div>
            <div>Issued {p ? formatDate(p.today, true) : ''}</div>
            {settled ? <div className="stamp">Paid</div> : null}
          </div>
        </header>

        <section className="receipt-guest">
          <div>
            <div className="eyebrow">Guest</div>
            <strong>{b.guest.fullName}</strong>
            {b.guest.phone ? <div>{b.guest.phone}</div> : null}
          </div>
          <div>
            <div className="eyebrow">Stay</div>
            <div>
              {formatDate(b.checkIn, true)} to {formatDate(b.checkOut, true)}
            </div>
            <div>
              {b.nights} night{b.nights === 1 ? '' : 's'} · {b.room ? `Room ${b.room.number}` : b.roomType.name}
            </div>
          </div>
        </section>

        <table className="receipt-table">
          <thead>
            <tr>
              <th>Description</th>
              <th className="r">Amount</th>
            </tr>
          </thead>
          <tbody>
            {r.lines.length === 0 ? (
              <tr>
                <td colSpan={2}>No charges yet.</td>
              </tr>
            ) : (
              r.lines.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.description}
                    <div className="receipt-sub">
                      {DEPARTMENT_LABEL[l.department] ?? l.department} · {formatDateTime(l.at)}
                    </div>
                  </td>
                  <td className="r num">{formatGhs(l.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <table className="receipt-totals">
          <tbody>
            <tr>
              <td>Subtotal (before tax)</td>
              <td className="r num">{formatGhs(r.net)}</td>
            </tr>
            {r.taxes.map((t) => (
              <tr key={t.code}>
                <td>
                  {t.name} ({t.rateBps / 100}%)
                </td>
                <td className="r num">{formatGhs(t.amount)}</td>
              </tr>
            ))}
            <tr className="strong">
              <td>Total</td>
              <td className="r num">{formatGhs(r.gross)}</td>
            </tr>
            {r.payments.map((pm) => (
              <tr key={pm.id}>
                <td>
                  {pm.description} <span className="receipt-sub">· {formatDateTime(pm.at)}</span>
                </td>
                <td className="r num">−{formatGhs(Math.abs(pm.amount))}</td>
              </tr>
            ))}
            <tr className="strong">
              <td>{r.balance > 0 ? 'Balance due' : r.balance < 0 ? 'Credit balance' : 'Balance'}</td>
              <td className="r num">{formatGhs(Math.abs(r.balance))}</td>
            </tr>
          </tbody>
        </table>

        <footer className="receipt-foot">Thank you for staying with us.{r.taxes.length ? ' Prices include the taxes and levies shown.' : ''}</footer>
      </article>
    </div>
  );
}
